import React, { useEffect, useState, useCallback } from "react";
import app, { auth, db } from "../Auth/firebase";
import {
  User,
  Phone,
  Clock,
  Award,
  Sparkles,
  PlusCircle,
  CreditCard,
  CheckCircle2,
  ShieldCheck,
  TrendingUp,
  Scale
} from "lucide-react";
import {
  doc,
  getDoc,
  collection,
  query,
  getDocs,
  orderBy,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CommonLoader from "../CommonLoader";

const formatDate = (value) => {
  try {
    if (!value) return "N/A";
    const d = value?.toDate ? value.toDate() : new Date(value);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "Invalid Date";
  }
};

const Passbook = () => {
  const [user, setUser] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [schemeRequests, setSchemeRequests] = useState(null);
  const [transactions, settransactions] = useState(null);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const schemeParam = queryParams.get("scheme") || "scheme1";

  const [selectedScheme, setSelectedScheme] = useState(schemeParam);

  useEffect(() => {
    if (schemeParam && schemeParam !== selectedScheme) {
      setSelectedScheme(schemeParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schemeParam]);

  const handleStartNewInvestment = () => {
    navigate(`/register?scheme=${selectedScheme}`);
  };

  const fetchUserData = async (uid) => {
    try {
      const docRef = doc(db, "users", uid);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        setCustomerData(docSnap.data());
      }
    } catch (err) {
      console.error("Error fetching customer data:", err);
    }
  };

  // ===================== Fetch payment doc ===================== //
  const fetchPaymentDoc = useCallback(async (uid, scheme = selectedScheme) => {
    try {
      const docRef = doc(db, `${scheme}_payments`, uid);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const request = {
          id: docSnap.id,
          ...docSnap.data(),
        };

        settransactions(request);
      } else {
        settransactions(null);
      }

      // FETCH REQUEST
      const docRef2 = doc(db, `${scheme}_request`, uid);
      const docSnap2 = await getDoc(docRef2);

      if (docSnap2.exists()) {
        const request = {
          id: docSnap2.id,
          ...docSnap2.data(),
        };

        setSchemeRequests(request);
      } else {
        setSchemeRequests(null);
      }
    } catch (err) {
      console.error("Error fetching by doc ID:", err);
    }
  }, [selectedScheme]);

  // ===================== Fetch payments doc ===================== //
  const [schemePayments, setSchemePayments] = useState([]);

  // Fetch scheme payments
  const fetchSchemePayments = useCallback(async (userId, scheme = selectedScheme) => {
    try {
      const schemesRef = collection(db, `${scheme}_payments`, userId, "schemes");
      const q = query(schemesRef, orderBy("date", "asc"));

      const querySnapshot = await getDocs(q);
      const payments = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        payments.push({
          id: doc.id,
          amount: data.amount,
          date: data.date,
          goldGrams: data.gold_gm,
          goldRate: data.current_rate_gm,
          instalment: data.instalment,
          month: data.month,
          paymentId: data.payment_id,
          goldpavan: data.current_rate_pvn,
        });
      });
      setSchemePayments(payments);
    } catch (err) {
      console.error("Error fetching scheme payments:", err);
    }
  }, [selectedScheme]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        await fetchUserData(firebaseUser.uid);
        await fetchSchemePayments(firebaseUser.uid, selectedScheme);
        await fetchPaymentDoc(firebaseUser.uid, selectedScheme);
      } else {
        setUser(null);
        setCustomerData(null);
        setSchemePayments([]);
        settransactions(null);
        setSchemeRequests(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [selectedScheme, fetchSchemePayments, fetchPaymentDoc]);

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [schemeSettings, setSchemeSettings] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);

  useEffect(() => {
    const fetchSchemeData = async () => {
      try {
        const docRef = doc(db, "schemes", "schemes");
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setSchemeSettings(docSnap.data() || {});
        }
      } catch (err) {
        console.error("Error fetching scheme settings:", err);
      }
    };
    fetchSchemeData();
  }, []);

  const hasBoughtThisMonth = schemePayments?.some(payment => {
    if (!payment.date) return false;
    const date = payment.date.toDate ? payment.date.toDate() : new Date(payment.date);
    const now = new Date();
    return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
  });
  const schemeDetail1 = schemeSettings?.[`${selectedScheme}_details`]?.[1] || "";
  const blockPayment = hasBoughtThisMonth && selectedScheme === "scheme1";

  const sendSchemePaymentConfirmation = async (paymentData) => {
    try {
      const functions = getFunctions(app, "asia-south1");
      const whatsappApi = httpsCallable(functions, "whatsappApi");

      const response = await whatsappApi({
        action: "sendSchemePayment",
        mobile: paymentData.mobile,
        name: paymentData.name,
        scheme: paymentData.scheme,
        amount: paymentData.amount,
        payment_date: paymentData.date,
      });

      console.log("WhatsApp API Response:", response.data);

      if (response.data?.success) {
        console.log(
          "Scheme payment notification sent successfully.",
          "Message ID:",
          response.data.data?.messageId,
        );
      } else {
        console.warn(
          "Failed to send scheme payment notification:",
          response.data?.message || "Unknown error",
        );
      }
    } catch (error) {
      console.error("Error sending scheme payment WhatsApp:", error);
    }
  };

  // ===================== Razorpay Payment Flow ===================== //
  const handlePaymentConfirm = async () => {
    if (schemeSettings) {
      if (blockPayment) {
        toast.error(schemeDetail1 || "Payment for this month is already completed for this scheme.");
        return;
      }

      const minKey = `min_${selectedScheme}`;
      const maxKey = `max_${selectedScheme}`;

      let min = Number(schemeSettings[minKey]);
      const condition0 = schemeSettings[`${selectedScheme}_details`]?.[0] || "";
      if (!min || isNaN(min)) {
        const minMatch = condition0.match(/\d+/);
        min = minMatch ? Number(minMatch[0]) : 0;
      }

      const max = Number(schemeSettings[maxKey] || Infinity);

      if (Number(paymentAmount) < min) {
        toast.error(condition0 || `Minimum payment is ₹${min} for this scheme.`);
        return;
      }

      if (Number(paymentAmount) > max) {
        toast.error(`Maximum payment is ₹${max} for this scheme.`);
        return;
      }
    }

    if (!paymentAmount || Number(paymentAmount) <= 0) {
      toast.warning("Please enter a valid amount");
      return;
    }

    if (!user) {
      toast.error("Please login to make a payment");
      return;
    }

    setPaymentLoading(true);

    try {
      const amountInPaise = Math.round(Number(paymentAmount) * 100);
      const orderDetails = {
        uid: user.uid,
        scheme: selectedScheme,
        name: customerData?.name || "N/A",
        mobile: customerData?.mobile || "N/A",
        email: transactions?.email || customerData?.email || "N/A",
        amount: Number(paymentAmount),
        scheme_details: selectedScheme,
        payment_no: transactions?.instalment || 1,
        graph_value: schemePayments?.length || 0,
      };

      const functions = getFunctions(app, "asia-south1");
      const createOrderId = httpsCallable(functions, "createOrderId");
      const orderResponse = await createOrderId({
        uid: user.uid,
        scheme: selectedScheme,
        amount: Number(paymentAmount),
        orderdetails: orderDetails,
      });

      const order = orderResponse.data;
      if (!order || !order.id) {
        toast.error("Failed to create payment order. Please try again.");
        setPaymentLoading(false);
        return;
      }

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: order.amount || amountInPaise,
        currency: order.currency || "INR",
        name: "Chirayil Gold And Diamonds",
        description: `${selectedScheme === "scheme1" ? "Scheme 1" : "Scheme 2"} - Gold Investment Payment`,
        order_id: order.id,
        handler: async function (response) {
          toast.success("Payment successful! Your passbook will be updated shortly.");
          setShowPaymentModal(false);

          // Send WhatsApp notification
          const formattedDate = new Date().toLocaleDateString("en-GB");
          sendSchemePaymentConfirmation({
            mobile: customerData?.mobile || "N/A",
            name: customerData?.name || "Customer",
            scheme: selectedScheme === "scheme1" ? "Scheme 1" : "Scheme 2",
            amount: paymentAmount,
            date: formattedDate,
          });

          setPaymentAmount("");
          setTimeout(async () => {
            if (user) {
              await fetchPaymentDoc(user.uid, selectedScheme);
              await fetchSchemePayments(user.uid, selectedScheme);
            }
          }, 3000);
        },
        prefill: {
          ...(customerData?.name ? { name: customerData.name } : {}),
          ...(customerData?.mobile && customerData.mobile !== "N/A" ? { contact: String(customerData.mobile) } : {}),
          ...((transactions?.email || customerData?.email) && (transactions?.email !== "N/A" && customerData?.email !== "N/A")
            ? { email: transactions?.email || customerData?.email }
            : {}),
        },
        notes: {
          uid: String(user.uid),
          scheme: String(selectedScheme),
          amount: String(paymentAmount),
          payment_no: String(transactions?.instalment || 1),
        },
        theme: {
          color: "#0b3a24",
        },
        modal: {
          ondismiss: function () {
            toast.info("Payment cancelled");
            setPaymentLoading(false);
          },
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on("payment.failed", function (response) {
        toast.error(`Payment failed: ${response.error.description}`);
        setPaymentLoading(false);
      });
      razorpay.open();
      setPaymentLoading(false);
    } catch (error) {
      console.error("Payment error:", error);
      toast.error(error.message || "Payment failed. Please try again.");
      setPaymentLoading(false);
    }
  };

  if (loading) return <CommonLoader />;

  return (
    <div className="min-h-screen bg-[#fafaf7] text-[#0b3a24] font-sans relative overflow-hidden">

      {/* Hero Header Banner (Dark Kerala Emerald Green & Gold) */}
      <section className="relative bg-gradient-to-br from-[#041a0f] via-[#09100d] to-[#020906] text-white pt-36 pb-20 px-4 sm:px-6 lg:px-8 border-b border-[#eddca6]/20 overflow-hidden">

        {/* Soft Ambient Light Glow Blobs */}
        <div className="absolute top-1/3 left-1/4 -translate-x-1/2 w-[500px] h-[500px] bg-gradient-to-tr from-gold-900/20 to-transparent rounded-full filter blur-[140px] pointer-events-none"></div>
        <div className="absolute bottom-10 right-10 w-[300px] h-[300px] bg-[#eddca6]/10 rounded-full filter blur-[100px] pointer-events-none"></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">

            {/* Title & Subtitle */}
            <div className="space-y-3">
              <div className="inline-flex items-center gap-2 bg-[#eddca6]/10 border border-[#eddca6]/30 text-[#eddca6] px-4 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest">
                <Sparkles className="w-3.5 h-3.5 text-[#eddca6]" />
                GOLD SAVINGS LEDGER
              </div>

              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-black tracking-widest uppercase text-white drop-shadow-md">
                INVESTMENT <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#eddca6] via-[#f5edd2] to-[#c5a85a]">PASSBOOK</span>
              </h1>

              <p className="text-xs sm:text-sm text-white/75 font-light leading-relaxed">
                Track your real-time gold weight deposits for <span className="font-bold text-[#eddca6] uppercase">{selectedScheme === "scheme1" ? "Scheme 1" : "Scheme 2"}</span>
              </p>
            </div>

            {/* Scheme Switcher Tabs */}
            <div className="flex items-center gap-2 bg-white/10 p-1.5 rounded-2xl border border-white/15 backdrop-blur-md">
              <button
                onClick={() => {
                  setSelectedScheme("scheme1");
                  navigate("/passbook?scheme=scheme1");
                }}
                className={`px-5 py-2.5 rounded-xl font-bold text-xs uppercase tracking-widest transition-all duration-300 ${selectedScheme === "scheme1"
                  ? "bg-gradient-to-r from-[#eddca6] via-[#fff3d1] to-[#eddca6] text-[#041a0f] shadow-lg font-sans scale-105"
                  : "text-white/80 hover:text-white hover:bg-white/10"
                  }`}
              >
                Scheme 1
              </button>

              <button
                onClick={() => {
                  setSelectedScheme("scheme2");
                  navigate("/passbook?scheme=scheme2");
                }}
                className={`px-5 py-2.5 rounded-xl font-bold text-xs uppercase tracking-widest transition-all duration-300 ${selectedScheme === "scheme2"
                  ? "bg-gradient-to-r from-[#eddca6] via-[#fff3d1] to-[#eddca6] text-[#041a0f] shadow-lg font-sans scale-105"
                  : "text-white/80 hover:text-white hover:bg-white/10"
                  }`}
              >
                Scheme 2
              </button>
            </div>

          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10 space-y-10">

        {/* Investor Profile Summary Card */}
        {customerData && (
          <div className="bg-white rounded-3xl p-6 sm:p-8 border border-gold-300 shadow-md flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-[#0b3a24] text-[#eddca6] rounded-2xl flex items-center justify-center shadow-md flex-shrink-0">
                <User className="w-7 h-7" />
              </div>
              <div>
                <span className="block text-[9px] font-bold text-[#0b3a24]/60 uppercase tracking-widest">Registered Investor</span>
                <h3 className="text-xl font-serif font-bold text-[#0b3a24]">{customerData.name || "N/A"}</h3>
                <span className="text-xs text-[#0b3a24]/75 font-light">Chirayil Gold Savings Account</span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-6 border-t md:border-t-0 md:border-l border-gold-200 pt-4 md:pt-0 md:pl-6 w-full md:w-auto">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#fafaf7] border border-gold-300 rounded-xl flex items-center justify-center text-[#0b3a24]">
                  <Phone className="w-4 h-4" />
                </div>
                <div>
                  <span className="block text-[9px] font-bold text-[#0b3a24]/60 uppercase tracking-widest">Phone Number</span>
                  <span className="text-xs font-bold text-[#0b3a24] font-sans">{customerData.mobile || "N/A"}</span>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#fafaf7] border border-gold-300 rounded-xl flex items-center justify-center text-[#0b3a24]">
                  <ShieldCheck className="w-4 h-4 text-emerald-600" />
                </div>
                <div>
                  <span className="block text-[9px] font-bold text-[#0b3a24]/60 uppercase tracking-widest">KYC Status</span>
                  <span className="text-xs font-bold text-emerald-700 font-sans">Verified Customer</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quick Stats Grid */}
        {transactions || (schemeRequests && schemeRequests.status === "active") ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

            {/* Stat 1: Latest Installment Amount */}
            <div className="bg-white border border-gold-300 rounded-3xl p-6 shadow-sm hover:shadow-xl hover:border-[#0b3a24]/30 transition-all duration-300 group">
              <div className="flex items-center justify-between mb-4">
                <span className="text-[10px] font-bold text-[#0b3a24]/60 uppercase tracking-widest">Latest Installment</span>
                <div className="w-10 h-10 bg-[#fafaf7] border border-gold-300 rounded-xl flex items-center justify-center group-hover:bg-[#0b3a24] transition-colors">
                  <CreditCard className="w-5 h-5 text-[#0b3a24] group-hover:text-[#eddca6] transition-colors" />
                </div>
              </div>
              <p className="text-3xl font-serif font-black text-[#0b3a24]">
                ₹{transactions?.amount ? Number(transactions.amount).toLocaleString("en-IN") : 0}
              </p>
              <div className="mt-3 pt-3 border-t border-gold-100 flex items-center justify-between text-[10px] font-semibold text-[#0b3a24]/70">
                <span>Last Deposit Date</span>
                <span>{transactions?.date ? formatDate(transactions.date) : "N/A"}</span>
              </div>
            </div>

            {/* Stat 2: Installments Completed */}
            <div className="bg-white border border-gold-300 rounded-3xl p-6 shadow-sm hover:shadow-xl hover:border-[#0b3a24]/30 transition-all duration-300 group">
              <div className="flex items-center justify-between mb-4">
                <span className="text-[10px] font-bold text-[#0b3a24]/60 uppercase tracking-widest">Completed Installments</span>
                <div className="w-10 h-10 bg-[#fafaf7] border border-gold-300 rounded-xl flex items-center justify-center group-hover:bg-[#0b3a24] transition-colors">
                  <CheckCircle2 className="w-5 h-5 text-[#0b3a24] group-hover:text-[#eddca6] transition-colors" />
                </div>
              </div>
              <p className="text-3xl font-serif font-black text-[#0b3a24]">
                {transactions?.instalment || schemePayments.length || 0}
              </p>
              <div className="mt-3 pt-3 border-t border-gold-100 flex items-center justify-between text-[10px] font-semibold text-[#0b3a24]/70">
                <span>Plan Tenure</span>
                <span>Monthly Deposits</span>
              </div>
            </div>

            {/* Stat 3: Running Total Savings */}
            <div className="bg-white border border-gold-300 rounded-3xl p-6 shadow-sm hover:shadow-xl hover:border-[#0b3a24]/30 transition-all duration-300 group">
              <div className="flex items-center justify-between mb-4">
                <span className="text-[10px] font-bold text-[#0b3a24]/60 uppercase tracking-widest">Running Savings Total</span>
                <div className="w-10 h-10 bg-[#fafaf7] border border-gold-300 rounded-xl flex items-center justify-center group-hover:bg-[#0b3a24] transition-colors">
                  <TrendingUp className="w-5 h-5 text-[#0b3a24] group-hover:text-[#eddca6] transition-colors" />
                </div>
              </div>
              <p className="text-3xl font-serif font-black text-emerald-800">
                ₹{transactions?.total_amt ? Number(transactions.total_amt).toLocaleString("en-IN") : schemePayments.reduce((sum, item) => sum + (Number(item.amount) || 0), 0).toLocaleString("en-IN")}
              </p>
              <div className="mt-3 pt-3 border-t border-gold-100 flex items-center justify-between text-[10px] font-semibold text-emerald-700">
                <span>Gold Rate Locked</span>
                <span>BIS 916 Standard</span>
              </div>
            </div>

          </div>
        ) : (schemeRequests && schemeRequests.status !== "active") ? (
          <div className="bg-white rounded-3xl p-12 text-center max-w-xl mx-auto border border-gold-300 shadow-xl space-y-4">
            <div className="w-16 h-16 bg-[#fafaf7] border border-gold-300 rounded-full flex items-center justify-center mx-auto text-[#0b3a24]">
              <Clock className="w-8 h-8" />
            </div>
            <h3 className="text-2xl font-serif font-bold text-[#0b3a24]">
              Request Pending Approval
            </h3>
            <p className="text-xs sm:text-sm text-[#0b3a24]/75 max-w-sm mx-auto font-light leading-relaxed">
              Your request to join this gold scheme is currently being processed by Chirayil showroom managers.
            </p>
            <div className="inline-block bg-amber-50 border border-amber-200 text-amber-800 text-[10px] font-bold px-4 py-2 rounded-lg tracking-widest uppercase">
              STATUS: {schemeRequests.status.toUpperCase()}
            </div>
            <div className="pt-4">
              <button
                onClick={() => navigate("/contact")}
                className="bg-[#0b3a24] hover:bg-[#0c482c] text-white font-bold px-8 py-3.5 rounded-xl text-xs uppercase tracking-widest transition-all shadow-md"
              >
                Contact Showroom
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-3xl p-12 text-center max-w-xl mx-auto border border-gold-300 shadow-xl space-y-4">
            <div className="w-16 h-16 bg-[#fafaf7] border border-gold-300 rounded-full flex items-center justify-center mx-auto text-[#0b3a24]">
              <Award className="w-8 h-8" />
            </div>
            <h3 className="text-2xl font-serif font-bold text-[#0b3a24]">
              No Active Plan Found
            </h3>
            <p className="text-xs sm:text-sm text-[#0b3a24]/75 max-w-sm mx-auto font-light leading-relaxed">
              You have not registered for <span className="font-bold uppercase">{selectedScheme}</span> yet. Register today to start saving in 916 gold.
            </p>
            <div className="pt-2">
              <button
                onClick={handleStartNewInvestment}
                className="gold-shine-btn bg-gradient-to-r from-[#0b3a24] via-[#0e482d] to-[#0b3a24] text-white font-bold px-8 py-4 rounded-xl text-xs uppercase tracking-widest transition-all shadow-lg hover:scale-105"
              >
                Start New Investment
              </button>
            </div>
          </div>
        )}

        {/* Ledger Transactions Table Section */}
        {(transactions || (schemeRequests && schemeRequests.status === "active")) && (
          <div className="bg-white rounded-3xl border border-gold-300 shadow-xl overflow-hidden">

            {/* Header Bar */}
            <div className="bg-gradient-to-r from-[#072a19] via-[#041a0f] to-[#020906] p-6 sm:p-8 text-white flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-[#eddca6]/20">
              <div>
                <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-[#eddca6] mb-1">
                  <Scale className="w-4 h-4 text-[#eddca6]" />
                  VERIFIED DEPOSIT LEDGER
                </div>
                <h3 className="text-2xl font-serif font-bold text-white">
                  Gold Weight Transactions
                </h3>
              </div>

              <button
                onClick={() => setShowPaymentModal(true)}
                className="font-sans font-extrabold text-xs uppercase tracking-widest px-8 py-3.5 rounded-xl shadow-xl transition-all flex items-center justify-center gap-2 gold-shine-btn bg-gradient-to-r from-[#eddca6] via-[#fff3d1] to-[#eddca6] text-[#041a0f] hover:scale-105 active:scale-95"
              >
                <PlusCircle className="w-4 h-4 text-[#041a0f]" />
                <span>Pay Monthly Deposit</span>
              </button>
            </div>

            {/* Transactions Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gold-200">
                <thead className="bg-[#fafaf7]">
                  <tr>
                    <th scope="col" className="px-8 lg:px-10 py-5 text-left text-xs font-bold text-[#0b3a24] uppercase tracking-widest border-r border-gold-200 w-16">
                      Sl No.
                    </th>
                    <th scope="col" className="px-8 lg:px-10 py-5 text-left text-xs font-bold text-[#0b3a24] uppercase tracking-widest border-r border-gold-200">
                      Instalment
                    </th>
                    <th scope="col" className="px-8 lg:px-10 py-5 text-left text-xs font-bold text-[#0b3a24] uppercase tracking-widest border-r border-gold-200">
                      Date
                    </th>
                    <th scope="col" className="px-8 lg:px-10 py-5 text-left text-xs font-bold text-[#0b3a24] uppercase tracking-widest border-r border-gold-200">
                      Gold Rate / Gram
                    </th>
                    <th scope="col" className="px-8 lg:px-10 py-5 text-left text-xs font-bold text-[#0b3a24] uppercase tracking-widest border-r border-gold-200">
                      Gold Rate / Pavan
                    </th>
                    <th scope="col" className="px-8 lg:px-10 py-5 text-left text-xs font-bold text-[#0b3a24] uppercase tracking-widest border-r border-gold-200">
                      Amount Paid
                    </th>
                    <th scope="col" className="px-8 lg:px-10 py-5 text-left text-xs font-bold text-[#0b3a24] uppercase tracking-widest">
                      Gold Weight (Grams)
                    </th>
                  </tr>
                </thead>

                <tbody className="bg-white divide-y divide-gold-100">
                  {schemePayments.length > 0 ? (
                    schemePayments.map((payment, index) => (
                      <tr
                        key={payment.id || index}
                        className="hover:bg-[#fafaf7] transition-all duration-150"
                      >
                        {/* Sl No */}
                        <td className="px-8 lg:px-10 py-6 whitespace-nowrap border-r border-gold-100 text-sm font-bold text-[#0b3a24] font-sans text-center">
                          {index + 1}
                        </td>

                        {/* Instalment number */}
                        <td className="px-8 lg:px-10 py-6 whitespace-nowrap border-r border-gold-100">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 bg-[#0b3a24] text-[#eddca6] rounded-xl flex items-center justify-center text-xs font-extrabold font-sans shadow-sm">
                              #{payment.instalment || index + 1}
                            </div>
                            <span className="text-sm font-bold text-[#0b3a24] font-sans">
                              Instalment {payment.instalment || index + 1}
                            </span>
                          </div>
                        </td>

                        {/* Date */}
                        <td className="px-8 lg:px-10 py-6 whitespace-nowrap border-r border-gold-100">
                          <div className="text-sm font-bold text-[#0b3a24]">
                            {payment.date ? formatDate(payment.date) : "N/A"}
                          </div>
                          <div className="text-xs text-[#0b3a24]/60 font-medium pt-0.5">
                            {payment.date
                              ? (payment.date.toDate ? payment.date.toDate() : new Date(payment.date)).toLocaleDateString("en-IN", { weekday: "short" })
                              : ""}
                          </div>
                        </td>

                        {/* Rate / Gram */}
                        <td className="px-8 lg:px-10 py-6 whitespace-nowrap border-r border-gold-100">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-[#0b3a24] font-sans">
                              ₹{payment.goldRate ? Number(payment.goldRate).toLocaleString("en-IN") : "N/A"}
                            </span>
                            <span className="px-2.5 py-1 bg-[#fafaf7] border border-gold-300 text-[10px] font-bold text-[#0b3a24] rounded-md">/gm</span>
                          </div>
                        </td>

                        {/* Rate / Pavan */}
                        <td className="px-8 lg:px-10 py-6 whitespace-nowrap border-r border-gold-100">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-[#0b3a24] font-sans">
                              ₹{payment.goldpavan ? Number(payment.goldpavan).toLocaleString("en-IN") : "N/A"}
                            </span>
                            <span className="px-2.5 py-1 bg-[#fafaf7] border border-gold-300 text-[10px] font-bold text-[#0b3a24] rounded-md">/pvn</span>
                          </div>
                        </td>

                        {/* Amount Paid */}
                        <td className="px-8 lg:px-10 py-6 whitespace-nowrap border-r border-gold-100">
                          <span className="text-sm font-black text-[#0d5c3a] bg-emerald-50 border border-emerald-300 px-4 py-2 rounded-xl font-sans inline-block shadow-sm">
                            ₹{payment.amount ? Number(payment.amount).toLocaleString("en-IN") : "N/A"}
                          </span>
                        </td>

                        {/* Gold Weight */}
                        <td className="px-8 lg:px-10 py-6 whitespace-nowrap">
                          <span className="text-sm font-black text-[#0b3a24] bg-[#fafaf7] border border-gold-300 px-4 py-2 rounded-xl font-sans inline-block shadow-sm">
                            {payment.goldGrams ? Number(payment.goldGrams).toFixed(3) + " g" : "N/A"}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center text-[#0b3a24]/70 font-light text-xs sm:text-sm">
                        No payments recorded yet. Click 'Pay Monthly Deposit' to make your first deposit.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Total Summary Footer (High-Contrast Luxury Redesign) */}
            <div className="bg-gradient-to-r from-[#041a0f] via-[#072a19] to-[#041a0f] px-6 sm:px-8 py-6 text-white border-t-2 border-[#eddca6]/40 shadow-inner">
              <div className="flex flex-col md:flex-row justify-between items-center gap-6">

                {/* Left Status Indicator */}
                <div className="bg-white/10 border border-[#eddca6]/30 px-5 py-2.5 rounded-2xl flex items-center gap-3 backdrop-blur-md shadow-sm">
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-400"></span>
                  </span>
                  <span className="text-xs text-white/90 font-medium font-sans">
                    Total Transactions Recorded: <span className="font-extrabold text-[#eddca6] font-sans text-sm ml-1">{schemePayments.length}</span>
                  </span>
                </div>

                {/* Right Stat Summary Cards */}
                <div className="flex flex-col sm:flex-row items-center gap-4 w-full md:w-auto justify-end">

                  {/* Total Savings Card */}
                  <div className="bg-gradient-to-br from-white/15 to-white/5 border border-[#eddca6]/40 rounded-2xl px-6 py-3.5 text-center min-w-[180px] w-full sm:w-auto shadow-lg backdrop-blur-md hover:border-[#eddca6] transition-all">
                    <span className="text-[10px] font-bold text-[#eddca6] uppercase tracking-widest block mb-0.5 font-sans">Total Savings</span>
                    <p className="text-xl sm:text-2xl font-black text-white font-sans drop-shadow-sm">
                      ₹{schemePayments.reduce((sum, item) => sum + (Number(item.amount) || 0), 0).toLocaleString("en-IN")}
                    </p>
                  </div>

                  {/* Total Gold Accumulation Card */}
                  <div className="bg-gradient-to-br from-[#eddca6]/20 via-white/10 to-[#eddca6]/10 border border-[#eddca6]/60 rounded-2xl px-6 py-3.5 text-center min-w-[200px] w-full sm:w-auto shadow-lg backdrop-blur-md hover:border-[#eddca6] transition-all">
                    <span className="text-[10px] font-bold text-[#eddca6] uppercase tracking-widest block mb-0.5 font-sans">Total Gold Accumulation</span>
                    <p className="text-xl sm:text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#eddca6] via-[#f5edd2] to-[#c5a85a] font-sans drop-shadow-sm">
                      {schemePayments.reduce((sum, item) => sum + (Number(item.goldGrams) || 0), 0).toFixed(3)} Grams
                    </p>
                  </div>

                </div>
              </div>
            </div>

          </div>
        )}

      </div>

      {/* Payment Deposit Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4 font-sans animate-fade-in">
          <div
            className="absolute inset-0 bg-[#041a0f]/85 backdrop-blur-md transition-opacity duration-300"
            onClick={() => {
              setShowPaymentModal(false);
              setPaymentAmount("");
            }}
          />

          <div className="relative bg-gradient-to-b from-[#072a19] via-[#041a0f] to-[#020906] rounded-3xl shadow-[0_25px_60px_-15px_rgba(0,0,0,0.9)] p-8 sm:p-10 w-full max-w-md border border-[#eddca6]/30 text-white overflow-hidden z-10">

            {/* Header */}
            <div className="mb-6 space-y-1">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[#eddca6]">ONLINE DEPOSIT</span>
              <h2 className="text-2xl font-serif font-bold text-white">
                Make Gold Investment
              </h2>
            </div>

            {/* Scheme Conditions */}
            {schemeSettings && (
              <div className="mb-6 bg-[#092e1d]/90 p-4 rounded-2xl border border-[#eddca6]/20 space-y-2">
                <h4 className="text-xs font-bold text-[#eddca6] uppercase tracking-wider">Scheme Terms & Conditions</h4>
                <ul className="list-disc pl-4 text-xs text-white/80 space-y-1 font-light">
                  {schemeSettings[`${selectedScheme}_details`]?.map((detail, idx) => (
                    <li key={idx}>{detail}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* If block payments */}
            {blockPayment ? (
              <div className="p-4 bg-red-950/70 text-red-200 border border-red-500/40 rounded-2xl mb-6 text-center text-xs font-semibold">
                {schemeDetail1 || "Monthly payment requirement already fulfilled for this scheme."}
              </div>
            ) : (
              <div className="space-y-3 mb-6">
                <label className="block text-[10px] font-bold text-[#eddca6] uppercase tracking-widest">
                  Enter Deposit Amount (₹)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    placeholder="Enter amount in ₹"
                    className="w-full px-5 py-4 bg-[#092e1d]/90 border border-[#eddca6]/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#eddca6]/40 focus:border-[#eddca6] text-white placeholder-white/40 text-sm font-semibold transition-all"
                  />
                </div>
              </div>
            )}

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  setPaymentAmount("");
                }}
                className="flex-1 py-3.5 border border-white/20 rounded-xl text-xs font-bold uppercase tracking-widest text-white/80 hover:bg-white/10 transition-colors"
              >
                Cancel
              </button>

              <button
                onClick={handlePaymentConfirm}
                disabled={blockPayment || paymentLoading}
                className={`gold-shine-btn flex-1 py-3.5 rounded-xl text-xs font-bold uppercase tracking-widest transition-all duration-300 ${blockPayment || paymentLoading
                  ? "bg-gray-600 text-gray-400 cursor-not-allowed"
                  : "bg-gradient-to-r from-[#eddca6] via-[#fff3d1] to-[#eddca6] text-[#041a0f] shadow-xl hover:scale-105"
                  }`}
              >
                {paymentLoading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#041a0f] border-t-transparent"></div>
                    <span>Processing...</span>
                  </div>
                ) : (
                  "Confirm Deposit"
                )}
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default Passbook;